import React from 'react';
import { useCustomerCreditSummary } from '../../features/customer-orders/api';
import './CustomerCreditSummary.scss';

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function displayMoney(value: number | null | undefined, loading: boolean) {
  if (loading) return 'Loading…';
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return money.format(value);
}

export function CustomerCreditSummary(): React.ReactElement {
  const { data, isPending, isError, refetch, isFetching } = useCustomerCreditSummary();

  if (isError) {
    return (
      <section className="customer-credit-summary" aria-label="Account credit overview">
        <div className="customer-credit-summary__notice" role="status">
          Credit information couldn't be loaded.
          <button
            type="button"
            className="customer-credit-summary__retry"
            onClick={() => void refetch()}
          >
            Try again
          </button>
        </div>
      </section>
    );
  }

  const values = [
    {
      label: 'Approved Credit',
      value: data?.approvedCredit,
      description: 'Credit limit approved for your account',
    },
    {
      label: 'Available Credit',
      value: data?.availableCredit,
      description: 'Credit currently available to use',
    },
    {
      label: 'Overdue Balance',
      value: data?.overdueBalance,
      description: 'Outstanding overdue invoice balance',
    },
  ];
  const noData = !isPending && values.every(({ value }) => value == null);
  const lastUpdated =
    data?.updatedAt && Number.isFinite(Date.parse(data.updatedAt))
      ? new Date(data.updatedAt).toLocaleString()
      : null;
  const isStale = Boolean(data?.staleAt);

  return (
    <section
      className="customer-credit-summary"
      aria-label="Account credit overview"
      aria-busy={isPending}
    >
      <div className="customer-credit-summary__grid">
        {values.map(({ label, value, description }) => (
          <div className="customer-credit-summary__card" key={label}>
            <div className="customer-credit-summary__label">{label}</div>
            <div className="customer-credit-summary__amount">{displayMoney(value, isPending)}</div>
            <div className="customer-credit-summary__description">{description}</div>
          </div>
        ))}
      </div>
      <p className="customer-credit-summary__footnote">
        {isPending
          ? 'Loading account credit details…'
          : noData
            ? 'Credit details are not available yet. Please contact your account representative.'
            : isStale
              ? `Last known values from Blume${lastUpdated ? ` (synced ${lastUpdated})` : ''}. The connection is currently out of date.`
              : `Credit information is synchronized from Blume${lastUpdated ? ` · Last updated ${lastUpdated}` : ''}.`}
        {isFetching && !isPending ? ' Refreshing…' : ''}
      </p>
    </section>
  );
}
